import {createActionCreators, ImmerReducer} from 'immer-reducer'
import {StatusCode} from 'status-code-enum'
import {PaginationState, SortingState} from '@tanstack/react-table'
import {select_gridSession} from './selectors'
import {PaginationDirection, Status} from 'commonTypes'
import defaultJobsState, {FiltersType, JobsStateType} from './state'
import {JobsGridSessionAC} from './gridSession.duck'
import {getJobDetails, getJobs} from 'api/graphQL/queries/jobs.queries'
import {JobRescheduleInput, LegionJob, ReportResult, ReportType} from 'graphql-api'
import {normalizeForApi, retrieveGraphQLJobs} from 'helpers/jobs'
import {genericRequestError, genericRequestSuccess, customMessageRequest} from 'redux/toast/toast.duck'
import {setJobsPermissibleForRescheduling} from 'redux/jobs/rescheduleJobs.duck'
import {jobReschedule} from 'api/graphQL/mutations/jobs.mutations'
import {getJobTypeActionOptions, getJobReport} from 'api/graphQL/queries/jobs.queries'
import apiRoutes from 'api/routes/paths'
import {downloadFile} from 'helpers/download'

class Jobs extends ImmerReducer<JobsStateType> {
  showFiltersToggled() {
    this.draftState.showFilters = !this.state.showFilters
  }
  showJobDetails() {
    this.draftState.showDetails = true
  }
  hideJobDetails() {
    this.draftState.showDetails = false
  }
  toggleJobDetails() {
    this.draftState.showDetails = !this.state.showDetails
  }
  jobIdToCopyApplied(jobId: string) {
    this.draftState.jobIdToCopy = jobId
  }
  jobIdCopiedTimerApplied(timer: ReturnType<typeof setTimeout>) {
    this.draftState.jobIdCopyTimer = timer
  }
  fetchJobsGO() {
    this.draftState.status = Status.PENDING
  }
  fetchJobsOK(jobs: LegionJob[]) {
    this.draftState.jobs = jobs
    this.draftState.status = Status.OK
  }
  fetchJobsERR(error) {
    this.draftState.error = error
    this.draftState.status = Status.REJECTED
  }
  fetchJobDetailsGO() {
    this.draftState.statusDetails = Status.PENDING
  }
  fetchJobDetailsOK(job: LegionJob) {
    this.draftState.error = defaultJobsState.error
    this.draftState.job = job
    this.draftState.statusDetails = Status.OK
  }
  fetchJobDetailsERR(error) {
    this.draftState.job = defaultJobsState.job
    this.draftState.error = error
    this.draftState.statusDetails = Status.REJECTED
  }

  rescheduleFailedJobsGO(){
    this.draftState.status = Status.PENDING
  }
  rescheduleFailedJobsOK(){
    this.draftState.status = Status.OK
  }
  rescheduleFailedJobsERR(error){
    this.draftState.error = error
    this.draftState.status = Status.REJECTED
  }

  fetchJobTypeActionOptionsGO() {
    this.draftState.jobTypeActionOptions.status = Status.PENDING
    this.draftState.jobTypeActionOptions.error = null
    this.draftState.jobTypeActionOptions.options = []
  }

  fetchJobTypeActionOptionsOK(options) {
    this.draftState.jobTypeActionOptions.status = Status.OK
    this.draftState.jobTypeActionOptions.error = null
    this.draftState.jobTypeActionOptions.options = options
  }

  fetchJobTypeActionOptionsERR(err) {
    this.draftState.jobTypeActionOptions.status = Status.REJECTED
    this.draftState.jobTypeActionOptions.error = err
    this.draftState.jobTypeActionOptions.options = []
  }
}

export const fetchJobs = () => async function(dispatch, getState) {
  const gridSession = select_gridSession(getState())
  const pageSize = gridSession.pagination.pageSize

  const {pagination, sorting, filters} = normalizeForApi(gridSession)

  dispatch(jobsAC.fetchJobsGO())
  try {
    const {data} = await getJobs(
      pagination,
      sorting,
      filters
    )

    const resultsCount = data.jobs?.totalCount ?? 0
    dispatch(JobsGridSessionAC.resultsCountApplied(resultsCount))

    const pageCount = Math.ceil(resultsCount/pageSize)
    dispatch(JobsGridSessionAC.pageCountApplied(pageCount))

    const pageInfo = data.jobs?.pageInfo
    if (pageInfo) {
      dispatch(JobsGridSessionAC.pageInfoApplied(pageInfo))
    }

    const jobs = retrieveGraphQLJobs(data)
    dispatch(jobsAC.fetchJobsOK(jobs))
    dispatch(setJobsPermissibleForRescheduling())
  } catch (err) {
    dispatch(genericRequestError())
    dispatch(jobsAC.fetchJobsERR(JSON.parse(JSON.stringify(err))))
  }
}

export const refreshJobs = (
  routerInstance,
  pagination?: PaginationState,
  sorting?: SortingState,
  filters?: FiltersType
) => async function(dispatch, getState) {
  const gridSession = select_gridSession(getState())

  const {
    globals: {params: stateParams, current: {name: stateName}},
    stateService,
  } = routerInstance

  dispatch(jobsAC.hideJobDetails())
  dispatch(jobsAC.jobIdToCopyApplied(''))

  const newPageIndex = pagination?.pageIndex
  const oldPageIndex = gridSession.pagination.pageIndex

  if (newPageIndex !== undefined && newPageIndex >= 0) {
    let cursor = ''
    if (newPageIndex > oldPageIndex) {
      cursor = gridSession.pageInfo?.endCursor ?? ''

      dispatch(JobsGridSessionAC.paginationDirectionApplied(PaginationDirection.NEXT))
      dispatch(JobsGridSessionAC.cursorApplied(cursor))
    } else if (newPageIndex < oldPageIndex) {
      cursor = gridSession.pageInfo?.startCursor ?? ''

      dispatch(JobsGridSessionAC.paginationDirectionApplied(PaginationDirection.PREV))
      dispatch(JobsGridSessionAC.cursorApplied(cursor))
    }
  }
  if(filters){
    dispatch(JobsGridSessionAC.filtersApplied(filters))
  }
  await stateService.go(stateName, {
    ...stateParams,
    pagination: pagination ?? gridSession.pagination,
    sorting: sorting ?? gridSession.sorting,
    filters: filters ?? gridSession.filters,
  })
}

export const fetchJobDetails = (jobId: string) => async function(dispatch) {
  dispatch(jobsAC.fetchJobDetailsGO())
  try {
    const {data} = await getJobDetails(jobId)
    const job: LegionJob = data.jobs?.nodes && Array.isArray(data.jobs?.nodes)
      ? data.jobs?.nodes[0]
      : {} as LegionJob
    if (data.jobs?.nodes?.length) {
      dispatch(jobsAC.fetchJobDetailsOK(job))
    } else if (!data.jobs?.nodes?.length) {
      throw new Error()
    }
  } catch (err) {
    dispatch(genericRequestError())
    dispatch(jobsAC.fetchJobDetailsERR(JSON.parse(JSON.stringify(err))))
  }
}

export const fetchJobTypeActionOptions = () => async function (dispatch) {
  dispatch(jobsAC.fetchJobTypeActionOptionsGO())
  try {
    const {data} = await getJobTypeActionOptions()
    if (data?.jobTypeActionOptions) {
      dispatch(jobsAC.fetchJobTypeActionOptionsOK(data?.jobTypeActionOptions))
    }
  } catch (err) {
    dispatch(jobsAC.fetchJobTypeActionOptionsERR(err))
  }
}
export const downloadJobReport = (jobId: string) => async (dispatch: any) => {
  try {
    const {data} = await getJobReport(jobId)

    console.log(data)

    const reportResult = data.downloadJobReport as ReportResult
    const reportData = reportResult.content
    if (!reportData) {
      throw new Error('No CSV data returned')
    }

    const type = reportResult.type == ReportType.Csv ? 'text/csv;charset=utf-8' : 'application/json;charset=utf-8'
    const ext = reportResult.type == ReportType.Csv ? 'csv' : 'json'

    const blob = new Blob([reportData], {type: type})

    downloadFile(blob, `job_report_${jobId}.${ext}`)

    dispatch(genericRequestSuccess())

  } catch (err) {
    dispatch(genericRequestError())
  }
}
export const downloadDataVerificationHashJson = (jobId: string) => async (dispatch: any) => {
  try {
    const response = await fetch(apiRoutes.dataVerificationHashJsonFileDownload(jobId))
    if (!response.ok) {
      if(response.status === StatusCode.ClientErrorNotFound) {
        dispatch(customMessageRequest('caution', 'File has already expired or the bucket contains no data to hash.'))
        return
      } else {
        throw new Error(`Failed to download file: ${response.statusText}`)
      }
    }
    const blob : Blob = await response.blob()

    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = `data_quality_json_${jobId}.json`
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-8'')?([^'"\s;]+)['"]?/i)
      if (filenameMatch?.[1]) {
        filename = decodeURIComponent(filenameMatch[1])
      }
    }

    downloadFile(blob, filename)

    dispatch(genericRequestSuccess())

  } catch (err) {
    dispatch(genericRequestError())
  }
}

export const rescheduleFailedJobs = (jobIds: [string], routerInstance) => async function(dispatch) {
  dispatch(jobsAC.rescheduleFailedJobsGO())
  try {
    const input: JobRescheduleInput = {jobIds}
    const {data} = await jobReschedule(input)
    if (data?.jobReschedule.successful) {
      dispatch(jobsAC.rescheduleFailedJobsOK())
      dispatch(genericRequestSuccess())
      await routerInstance.stateService.reload()
    } else {
      throw {message: 'Something went wrong when rescheduling jobs', status: StatusCode.SuccessAccepted}
    }
  } catch (err) {
    dispatch(genericRequestError())
    dispatch(jobsAC.rescheduleFailedJobsERR(JSON.parse(JSON.stringify(err))))
  }
}

export const jobsAC = createActionCreators(Jobs)

export default Jobs
